'use client';
import * as React from 'react';
import { Track, RoomEvent } from 'livekit-client';
import {
  useMaybeLayoutContext,
  MediaDeviceMenu,
  TrackToggle,
  useRoomContext,
  useIsRecording,
} from '@livekit/components-react';
import { useKrispNoiseFilter } from '@livekit/components-react/krisp';
import styles from '../styles/SettingsMenu.module.css';

/**
 * @alpha
 */
export interface SettingsMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Whether the microphone control is disabled (e.g., by server-side mute).
   */
  microphoneDisabled?: boolean;
  /**
   * Reason why the microphone is disabled.
   */
  microphoneDisabledReason?: string;
  /**
   * Callback when microphone toggle is attempted. Return false to prevent the action.
   */
  onMicrophoneToggle?: (enabled: boolean) => boolean;
}

/**
 * @alpha
 */
export function SettingsMenu({
  microphoneDisabled: externalMicrophoneDisabled,
  microphoneDisabledReason: externalMicrophoneDisabledReason,
  onMicrophoneToggle: externalOnMicrophoneToggle,
  ...props
}: SettingsMenuProps) {
  const layoutContext = useMaybeLayoutContext();
  const room = useRoomContext();
  const recordingEndpoint = process.env.NEXT_PUBLIC_LK_RECORD_ENDPOINT;
  
  // Internal state for microphone control disabled (from data messages or track events)
  const [isMicrophoneControlDisabled, setIsMicrophoneControlDisabled] = React.useState(false);
  const [microphoneDisabledReason, setMicrophoneDisabledReason] = React.useState<string>('');
  const [lastDataMessageSequence, setLastDataMessageSequence] = React.useState<number>(0);
  const userInitiatedMuteRef = React.useRef<boolean>(false);
  
  // Use external props if provided, otherwise use internal state
  const microphoneDisabled = externalMicrophoneDisabled ?? isMicrophoneControlDisabled;
  const microphoneDisabledReasonFinal = externalMicrophoneDisabledReason ?? microphoneDisabledReason;
  const onMicrophoneToggle = externalOnMicrophoneToggle;
  
  // Listen for data messages to disable/enable microphone control
  React.useEffect(() => {
    const handleDataReceived = (payload: Uint8Array, participant: any, kind: any, topic: string | undefined) => {
      try {
        const decoder = new TextDecoder();
        const messageText = decoder.decode(payload);
        const message = JSON.parse(messageText);

        if (message.type === 'MICROPHONE_CONTROL' && message.payload) {
          const { disabled, reason, timestamp, sequence } = message.payload;

          // Deduplicate messages using sequence number
          if (sequence && sequence <= lastDataMessageSequence) {
            console.log('Ignoring duplicate data message:', sequence);
            return;
          }

          if (sequence) {
            setLastDataMessageSequence(sequence);
          }

          setIsMicrophoneControlDisabled(disabled);
          setMicrophoneDisabledReason(reason || 'server_muted');
        }
      } catch (error) {
        console.error('Error parsing data message:', error);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, lastDataMessageSequence]);
  
  // Fallback: Listen for TrackMuted events
  React.useEffect(() => {
    const handleTrackMuted = (publication: any) => {
      if (publication.source === Track.Source.Microphone && !userInitiatedMuteRef.current) {
        setIsMicrophoneControlDisabled(true);
        setMicrophoneDisabledReason('server_muted');
      } else {
        userInitiatedMuteRef.current = false;
      }
    };

    const handleTrackUnmuted = (publication: any) => {
      if (publication.source === Track.Source.Microphone && !userInitiatedMuteRef.current) {
        setIsMicrophoneControlDisabled(false);
        setMicrophoneDisabledReason('');
      } else {
        userInitiatedMuteRef.current = false;
      }
    };

    room.localParticipant.on('trackMuted', handleTrackMuted);
    room.localParticipant.on('trackUnmuted', handleTrackUnmuted);

    return () => {
      room.localParticipant.off('trackMuted', handleTrackMuted);
      room.localParticipant.off('trackUnmuted', handleTrackUnmuted);
    };
  }, [room]);

  const settings = React.useMemo(() => {
    return {
      media: { camera: true, microphone: true, label: 'Media Devices', speaker: true },
      effects: { label: 'Effects' },
      recording: recordingEndpoint ? { label: 'Recording' } : undefined,
      streaming: { label: 'Streaming' },
    };
  }, []);

  const tabs = React.useMemo(
    () => Object.keys(settings).filter((t) => t !== undefined) as Array<keyof typeof settings>,
    [settings],
  );
  const [activeTab, setActiveTab] = React.useState(tabs[0]);

  const { isNoiseFilterEnabled, setNoiseFilterEnabled, isNoiseFilterPending } =
    useKrispNoiseFilter();

  React.useEffect(() => {
    // enable Krisp by default
    setNoiseFilterEnabled(true);
  }, []);

  const isRecording = useIsRecording();
  const [initialRecStatus, setInitialRecStatus] = React.useState(isRecording);
  const [processingRecRequest, setProcessingRecRequest] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [processingStreamRequest, setProcessingStreamRequest] = React.useState(false);


  React.useEffect(() => {
    if (initialRecStatus !== isRecording) {
      setProcessingRecRequest(false);
    }
  }, [isRecording, initialRecStatus]);

  const toggleRoomRecording = async () => {
    if (!recordingEndpoint) {
      throw TypeError('No recording endpoint specified');
    }
    if (room.isE2EEEnabled) {
      throw Error('Recording of encrypted meetings is currently not supported');
    }
    setProcessingRecRequest(true);
    setInitialRecStatus(isRecording);
    let response: Response;
    if (isRecording) {
      response = await fetch(recordingEndpoint + `/stop?roomName=${room.name}`);
    } else {
      response = await fetch(recordingEndpoint + `/start?roomName=${room.name}`);
    }
    if (response.ok) {
      // Wait for the recording status to update
      setTimeout(() => {
        setProcessingRecRequest(false);
      }, 1000);
    } else {
      console.error(
        'Error handling recording request, check server logs:',
        response.status,
        response.statusText,
      );
      setProcessingRecRequest(false);
    }
  };

  const toggleRoomStreaming = async () => {
    if (room.isE2EEEnabled) {
      throw Error('Streaming of encrypted meetings is currently not supported');
    }
    setProcessingStreamRequest(true);
    let response: Response;
    if (isStreaming) {
      response = await fetch(`/api/stream/stop?roomName=${room.name}`);
    } else {
      response = await fetch(`/api/stream/start?roomName=${room.name}`);
    }
    if (response.ok) {
      // Immediately update UI state
      setIsStreaming(!isStreaming);
      // Wait for the streaming status to update
      setTimeout(async () => {
        try {
          const statusResponse = await fetch(`/api/stream/status?roomName=${room.name}`);
          if (statusResponse.ok) {
            const data = await statusResponse.json();
            setIsStreaming(data.isStreaming);
          }
        } catch (error) {
          console.error('Error checking streaming status:', error);
        }
        setProcessingStreamRequest(false);
      }, 1000);
    } else {
      console.error(
        'Error handling streaming request, check server logs:',
        response.status,
        response.statusText,
      );
      setProcessingStreamRequest(false);
    }
  };

  return (
    <div className="settings-menu" style={{ width: '100%' }} {...props}>
      <div className={styles.tabs}>
        {tabs.map(
          (tab) =>
            settings[tab] && (
              <button
                className={`${styles.tab} lk-button`}
                key={tab}
                onClick={() => setActiveTab(tab)}
                aria-pressed={tab === activeTab}
              >
                {
                  // @ts-ignore
                  settings[tab].label
                }
              </button>
            ),
        )}
      </div>
      <div className="tab-content">
        {activeTab === 'media' && (
          <>
            {settings.media && settings.media.camera && (
              <>
                <h3>Camera</h3>
                <section className="lk-button-group">
                  <TrackToggle source={Track.Source.Camera}>Camera</TrackToggle>
                  <div className="lk-button-group-menu">
                    <MediaDeviceMenu kind="videoinput" />
                  </div>
                </section>
              </>
            )}
            {settings.media && settings.media.microphone && (
              <>
                <h3>Microphone</h3>
                <section className={`lk-button-group ${microphoneDisabled ? 'lk-microphone-disabled' : ''}`}>
                  {/* See ControlBar: scopes the disabled styling to the toggle so
                      the neighbouring device picker stays usable. */}
                  <div
                    className="lk-media-toggle"
                    style={{
                      position: 'relative',
                      opacity: microphoneDisabled ? 0.5 : 1,
                      pointerEvents: microphoneDisabled ? 'none' : 'auto',
                      cursor: microphoneDisabled ? 'not-allowed' : 'pointer',
                    }}
                    title={microphoneDisabled ? 'Microphone is disabled. A moderator has muted you.' : undefined}
                    aria-label={microphoneDisabled ? 'Microphone is disabled. A moderator has muted you.' : undefined}
                    aria-describedby={microphoneDisabled ? 'microphone-disabled-tooltip-settings' : undefined}
                  >
                    <TrackToggle
                      source={Track.Source.Microphone}
                      onChange={(enabled: boolean, isUserInitiated: boolean) => {
                        // Prevent unmute if microphone is disabled by server
                        if (!enabled && microphoneDisabled && isUserInitiated) {
                          console.warn('Cannot unmute microphone: disabled by server');
                          return; // Prevent the action
                        }
                        
                        // Check with parent component if action should be allowed
                        if (isUserInitiated && onMicrophoneToggle) {
                          const shouldAllow = onMicrophoneToggle(enabled);
                          if (!shouldAllow) {
                            return; // Prevent the action
                          }
                        }
                      }}
                    >
                      Microphone
                    </TrackToggle>
                    {microphoneDisabled && (
                      <span
                        id="microphone-disabled-tooltip-settings"
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginBottom: '4px',
                          padding: '4px 8px',
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        }}
                        className="lk-microphone-tooltip"
                      >
                        Microphone is disabled. A moderator has muted you.
                      </span>
                    )}
                  </div>
                  <div className="lk-button-group-menu">
                    <MediaDeviceMenu kind="audioinput" />
                  </div>
                </section>
              </>
            )}
            {settings.media && settings.media.speaker && (
              <>
                <h3>Speaker & Headphones</h3>
                <section className="lk-button-group">
                  <span className="lk-button">Audio Output</span>
                  <div className="lk-button-group-menu">
                    <MediaDeviceMenu kind="audiooutput"></MediaDeviceMenu>
                  </div>
                </section>
              </>
            )}
          </>
        )}
        {activeTab === 'effects' && (
          <>
            <h3>Audio</h3>
            <section>
              <label htmlFor="noise-filter"> Enhanced Noise Cancellation</label>
              <input
                type="checkbox"
                id="noise-filter"
                onChange={(ev) => setNoiseFilterEnabled(ev.target.checked)}
                checked={isNoiseFilterEnabled}
                disabled={isNoiseFilterPending}
              ></input>
            </section>
          </>
        )}
        {activeTab === 'recording' && (
          <>
            <h3>Record Meeting</h3>
            <section>
              <p>
                {isRecording
                  ? 'Meeting is currently being recorded'
                  : 'No active recordings for this meeting'}
              </p>
              <button disabled={processingRecRequest} onClick={() => toggleRoomRecording()}>
                {isRecording ? 'Stop' : 'Start'} Recording
              </button>
            </section>
          </>
        )}
        {activeTab === 'streaming' && (
          <>
            <h3>Stream Meeting</h3>
            <section>
              <p>
                {isStreaming
                  ? 'Meeting is currently being streamed'
                  : 'No active streams for this meeting'}
              </p>
              <button disabled={processingStreamRequest} onClick={() => toggleRoomStreaming()}>
                {isStreaming ? 'Stop' : 'Start'} Streaming
              </button>
            </section>
          </>
        )}
      </div>
      <button
        className={`lk-button ${styles.settingsCloseButton}`}
        onClick={() => layoutContext?.widget.dispatch?.({ msg: 'toggle_settings' })}
      >
        Close
      </button>
    </div>
  );
}
